'use strict';

import {applicationModel} from "../../models/applicationmodel.js";
import {fieldModel} from "../../models/fieldmodel.js";

export class ApplicationAssessmentCtrl {
	$scope;
	$filter;
	jsDataService;
	$mdDialog;

	saveDataUrl;
	assessmentData = {};
	grantDetermined = '';
	grantConfirmed = '';
	item = {};
	fieldCfg = {};

	constructor($scope, $filter, jsDataService, $mdDialog) {
		'ngInject';
		this.$scope = $scope;
		this.$filter = $filter;
		this.jsDataService = jsDataService;
		this.$mdDialog = $mdDialog;

		this.saveDataUrl = jsDataService.get('cfgSaveUrl');

		const defaultData = jsDataService.get('assessmentData');
		this.initializeData(jsDataService.get('applicationBase', {}), defaultData);
	}

	initializeData(baseData, defaultData = {}) {
		this.item = new applicationModel(baseData, this.$filter, this.jsDataService);
		this.makeFieldCfg(this.item.fields);
		const classifiers = defaultData.classifiers || {};

		this.classifierMap = {
			admissibility: classifiers.admissibility.reduce((map, title, index) => {
				map[index] = title;
				return map;
			}, {}),
			eligibility: classifiers.eligibility.reduce((map, title, index) => {
				map[index] = title;
				return map;
			}, {}),
			award_criteria: classifiers.award_criteria.reduce((map, title, index) => {
				map[index] = title;
				return map;
			}, {}),
			financial_capacity: classifiers.financial_capacity.reduce((map, title, index) => {
				map[index] = title;
				return map;
			}, {}),
			rejection: classifiers.rejection || []
		};

		this.assessmentData = {
			admissibility_check: {
				date: defaultData.admissibility_check_date > 0
					? new Date(defaultData.admissibility_check_date * 1000).toISOString()
					: '',
				result: defaultData.admissibility_check_result || '',
				ifRejected: defaultData.admissibility_check_if_rejected || '',
				otherReason: defaultData.admissibility_check_other_reason || '',
				classifier: classifiers.admissibility.map((title, id) => ({ id, title })) || [],
				rejectClassifier: (classifiers.rejection.admissibility_check || []).map(item => ({
					id: item.id,
					title: item.title,
				})),
			},
			eligibility_check: {
				date: defaultData.eligibility_check_date > 0
					? new Date(defaultData.eligibility_check_date * 1000).toISOString()
					: '',
				result: defaultData.eligibility_check_result || '',
				ifRejected: defaultData.eligibility_check_if_rejected || '',
				otherReason: defaultData.eligibility_check_other_reason || '',
				classifier: classifiers.eligibility.map((title, id) => ({ id, title })) || [],
				rejectClassifier: (classifiers.rejection.eligibility_check || []).map(item => ({
					id: item.id,
					title: item.title,
				}))
			},
			award_criteria: {
				date: defaultData.award_criteria_date > 0
					? new Date(defaultData.award_criteria_date * 1000).toISOString()
					: '',
				result: defaultData.award_criteria_result || '',
				ifRejected: defaultData.award_criteria_if_rejected || '',
				otherReason: defaultData.award_criteria_other_reason || '',
				classifier: classifiers.award_criteria.map((title, id) => ({ id, title })) || [],
				rejectClassifier: (classifiers.rejection.award_criteria || []).map(item => ({
					id: item.id,
					title: item.title,
				}))
			},
			financial_capacity: {
				date: defaultData.financial_capacity_date > 0
					? new Date(defaultData.financial_capacity_date * 1000).toISOString()
					: '',
				result: defaultData.financial_capacity_result || '',
				classifier: classifiers.financial_capacity.map((title, id) => ({ id, title })) || [],
			},
			coordinator_is_public: {
				result: defaultData.coordinator_is_public !== undefined ? defaultData.coordinator_is_public : '',
				displayValue: this.getPublicCoordinatorText(defaultData.coordinator_is_public),
			},
			project_assessment_committee_date: defaultData.project_assessment_committee_date > 0
				? this.toLocalISOString(defaultData.project_assessment_committee_date)
				: '',
			project_selection_committee_date: defaultData.project_selection_committee_date > 0
				? this.toLocalISOString(defaultData.project_selection_committee_date)
				: '',
			decision_for_financing_date: defaultData.decision_for_financing_date > 0
				? this.toLocalISOString(defaultData.decision_for_financing_date)
				: '',
			assessment_comment: defaultData.assessment_comment
		};

		this.grantDetermined = defaultData.grant_determined || '';
		this.grantConfirmed = defaultData.grant_confirmed || '';
	}

	toLocalISOString(timestamp) {
		const utcDate = new Date(timestamp * 1000);

		const cetOffset = this.getCETOffset(utcDate);
		const localDate = new Date(utcDate.getTime() + cetOffset);

		return localDate.toISOString().split('T')[0];
	}

	getCETOffset(date) {
		const tz = 'Europe/Paris';
		const options = { timeZone: tz, timeZoneName: 'short' };
		const fmt = new Intl.DateTimeFormat('en-US', options);
		const parts = fmt.formatToParts(date);
		const tzName = parts.find(part => part.type === 'timeZoneName')?.value;

		return tzName === 'CEST' ? 2 * 3600 * 1000 : 1 * 3600 * 1000;
	}

	getRejectionTitle(rowKey, id) {
		const classifiers = this.classifierMap.rejection[rowKey];
		if (!classifiers) {
			return '-';
		}

		const rejection = classifiers.find(item => String(item.id) === String(id));
		return rejection ? rejection.title : '-';
	}

	getClassifierTitle(classifierKey, id) {
		return this.classifierMap[classifierKey]?.[id] || '-';
	}

	makeFieldCfg(fields) {
		if (fields) {
			for (let field of Object.values(fields)) {
				this.fieldCfg[field.name] = new fieldModel(field);
			}
		}
	}

	isField(name) {
		return Boolean(this.fieldCfg[name]);
	}

	getFieldTitle(name) {
		return this.isField(name) ? this.fieldCfg[name].title : '';
	}

	editRow(ev, rowKey) {
		const row = angular.copy(this.assessmentData[rowKey]);

		this.$mdDialog.show({
			controller: ['$scope', '$mdDialog', 'row', 'rowKey', ($scope, $mdDialog, row, rowKey) => {
				$scope.row = row;
				$scope.rowKey = rowKey;

				$scope.$watch('row.result', function(newValue, oldValue) {
					if (newValue != 0) {
						$scope.row.ifRejected = null;
						$scope.row.customIfRejected = null;
					}
				});


				$scope.save = () => $mdDialog.hide($scope.row);
				$scope.close = () => $mdDialog.cancel();
			}],
			templateUrl: 'assessment_editor.html',
			parent: angular.element(document.body),
			targetEvent: ev,
			clickOutsideToClose: true,
			fullscreen: false,
			locals: { row, rowKey }
		}).then(updatedRow => {
			this.assessmentData[rowKey] = updatedRow;

			if (rowKey === 'coordinator_is_public') {
				this.assessmentData[rowKey].displayValue = this.getPublicCoordinatorText(updatedRow.result);
			}

			this.saveData(this.preparePayload());
		});
	}

	preparePayload() {
		return {
			grant_determined: this.grantDetermined || '',
			grant_confirmed: this.grantConfirmed || '',
			admissibility_check_date: this.convertDateToTimestamp(this.assessmentData.admissibility_check.date) || 0,
			admissibility_check_result: this.assessmentData.admissibility_check.result ?? 0,
			admissibility_check_if_rejected: this.assessmentData.admissibility_check.ifRejected || '',
			admissibility_check_other_reason: this.assessmentData.admissibility_check.otherReason || '',

			eligibility_check_date: this.convertDateToTimestamp(this.assessmentData.eligibility_check.date) || 0,
			eligibility_check_result: this.assessmentData.eligibility_check.result ?? 0,
			eligibility_check_if_rejected: this.assessmentData.eligibility_check.ifRejected || '',
			eligibility_check_other_reason: this.assessmentData.eligibility_check.otherReason || '',

			award_criteria_date: this.convertDateToTimestamp(this.assessmentData.award_criteria.date) || 0,
			award_criteria_result: this.assessmentData.award_criteria.result ?? 0,
			award_criteria_if_rejected: this.assessmentData.award_criteria.ifRejected || '',
			award_criteria_other_reason: this.assessmentData.award_criteria.otherReason || '',

			coordinator_is_public: this.assessmentData.coordinator_is_public.result !== ''
				? parseInt(this.assessmentData.coordinator_is_public.result, 10)
				: 0,

			financial_capacity_date: this.convertDateToTimestamp(this.assessmentData.financial_capacity.date) || 0,
			financial_capacity_result: this.assessmentData.financial_capacity.result ?? 0,
			assessment_comment: this.assessmentData.assessment_comment || this.assessmentData.financial_capacity.text || '',

			project_assessment_committee_date: this.convertDateToTimestamp(this.assessmentData.project_assessment_committee_date) || 0,
			project_selection_committee_date: this.convertDateToTimestamp(this.assessmentData.project_selection_committee_date) || 0,
			decision_for_financing_date: this.convertDateToTimestamp(this.assessmentData.decision_for_financing_date) || 0
		};
	}

	saveData(data) {
		if (!this.saveDataUrl) {
			return;
		}

		this.$scope.basicHttpConfigProcess({
			url: this.saveDataUrl,
			method: 'POST',
			data: data
		}).then(response => {
			if (response.data.success) {

			}
		}).catch(error => console.error('Save error:', error));
	}

	convertDateToTimestamp(date) {
		if (!date) {
			return null;
		}
		const timestamp = Math.floor(new Date(date).getTime() / 1000);
		if (isNaN(timestamp)) {
			return null;
		}
		return timestamp;
	}

	editGrantDetermined(ev) {
		const currentValue = angular.copy(this.grantDetermined);

		this.$mdDialog.show({
			controller: ['$scope', '$mdDialog', 'currentValue', function ($scope, $mdDialog, currentValue) {
				$scope.value = currentValue;

				$scope.save = function () {
					$mdDialog.hide($scope.value);
				};

				$scope.close = function () {
					$mdDialog.cancel();
				};
			}],
			template: `
			<md-dialog aria-label="Edit Grant Determined" flex flex-gt-sm="60" flex-gt-md="40">
				<md-toolbar>
					<div class="md-toolbar-tools">
						<h2 style="color:#fff">Edit grant determined</h2>
						<span flex></span>
						<md-button class="md-icon-button" ng-click="close()">
						<md-icon>close</md-icon>
						</md-button>
					</div>
				</md-toolbar>
				<md-dialog-content>
					<div class="md-dialog-content">
						<md-input-container class="md-block mb-40" flex md-no-float="true">
							<fieldset>
								<legend aria-label="Grant determined">Grant determined</legend>
								<md-input-container class="md-block">
								<input ng-model="value" type="number" step="any">
								</md-input-container>
							</fieldset>
						</md-input-container>
					</div>
				</md-dialog-content>
					<md-dialog-actions layout="row">
					<span flex></span>
					<md-button ng-click="close()">Cancel</md-button>
					<md-button ng-click="save()" class="md-raised md-accent">Save</md-button>
				</md-dialog-actions>
			</md-dialog>
        `,
			parent: angular.element(document.body),
			targetEvent: ev,
			clickOutsideToClose: true,
			fullscreen: false,
			locals: { currentValue }
		}).then(updatedValue => {
			this.grantDetermined = updatedValue;

			this.saveData(this.preparePayload());
		});
	}

	editgrantConfirmed(ev) {
		const currentValue = angular.copy(this.grantConfirmed);

		this.$mdDialog.show({
			controller: ['$scope', '$mdDialog', 'currentValue', function ($scope, $mdDialog, currentValue) {
				$scope.value = currentValue;

				$scope.save = function () {
					$mdDialog.hide($scope.value);
				};

				$scope.close = function () {
					$mdDialog.cancel();
				};
			}],
			template: `
			<md-dialog aria-label="Edit Grant Determined" flex flex-gt-sm="60" flex-gt-md="40">
				<md-toolbar>
					<div class="md-toolbar-tools">
						<h2 style="color:#fff">Edit grant confirmed</h2>
						<span flex></span>
						<md-button class="md-icon-button" ng-click="close()">
						<md-icon>close</md-icon>
						</md-button>
					</div>
				</md-toolbar>
				<md-dialog-content>
					<div class="md-dialog-content">
						<md-input-container class="md-block mb-40" flex md-no-float="true">
							<fieldset>
								<legend aria-label="Grant determined">Grant confirmed</legend>
								<md-input-container class="md-block">
								<input ng-model="value" type="number" step="any">
								</md-input-container>
							</fieldset>
						</md-input-container>
					</div>
				</md-dialog-content>
					<md-dialog-actions layout="row">
					<span flex></span>
					<md-button ng-click="close()">Cancel</md-button>
					<md-button ng-click="save()" class="md-raised md-accent">Save</md-button>
				</md-dialog-actions>
			</md-dialog>
        `,
			parent: angular.element(document.body),
			targetEvent: ev,
			clickOutsideToClose: true,
			fullscreen: false,
			locals: { currentValue }
		}).then(updatedValue => {
			this.grantConfirmed = updatedValue;
			this.saveData(this.preparePayload());
		});
	}

	editField(ev, fieldKey, isTextArea = false) {
		let currentValue;

		if (fieldKey === 'financial_capacity') {

			currentValue = {
				date: this.assessmentData.financial_capacity.date ? new Date(this.assessmentData.financial_capacity.date) : null,
				result: this.assessmentData.financial_capacity.result || '',
				text: this.assessmentData.assessment_comment || '',
				classifier: this.assessmentData.financial_capacity.classifier || []
			};
		} else {
			currentValue = angular.copy(this.assessmentData[fieldKey]);
		}

		this.$mdDialog.show({
			controller: ['$scope', '$mdDialog', 'currentValue', function ($scope, $mdDialog, currentValue) {
				$scope.value = angular.copy(currentValue);

				if ($scope.value && $scope.value.date && typeof $scope.value.date === 'string') {
					$scope.value.date = new Date($scope.value.date);
				}

				$scope.save = function () {
					let updatedValue = angular.copy($scope.value);

					$mdDialog.hide(updatedValue);
				};

				$scope.close = function () {
					$mdDialog.cancel();
				};
			}],
			template: `
            <md-dialog aria-label="Edit Field" flex flex-gt-sm="60" flex-gt-md="40">
                <md-toolbar>
                    <div class="md-toolbar-tools">
                        <h2 style="color:#fff">Edit</h2>
                        <span flex></span>
                        <md-button class="md-icon-button" ng-click="close()">
                            <md-icon>close</md-icon>
                        </md-button>
                    </div>
                </md-toolbar>
                <md-dialog-content>
                    <div class="md-dialog-content">
                        ${
				isTextArea
					? `
									<md-input-container class="md-block mb-20" flex>
										<fieldset class="fieldset-style">
											<legend aria-label="Date">Date</legend>
											<md-datepicker ng-model="value.date" md-open-on-focus aria-label="Date"></md-datepicker>
										</fieldset>
									</md-input-container>
                                    <md-input-container class="md-block mb-20" flex>
                                        <fieldset>
                                            <legend aria-label="Assessment Result">Result</legend>
                                            <md-select ng-model="value.result" placeholder="Select Result">
                                                <md-option ng-repeat="option in value.classifier" ng-value="option.id">
                                                    {{ option.title }}
                                                </md-option>
                                            </md-select>
                                        </fieldset>
                                    </md-input-container>

                                    <md-input-container class="md-block mb-40" flex>
                                        <fieldset class="h-auto">
                                            <legend aria-label="Comment">Comment</legend>
                                            <textarea
                                                ng-model="value.text"
                                                md-maxlength="1000"
                                                maxlength="1000"
                                                rows="5"
                                                placeholder="Enter comment here">
                                            </textarea>
                                        </fieldset>
                                    </md-input-container>
                                `
					: `
                                    <md-input-container class="md-block mb-40" flex>
                                        <fieldset class="fieldset-style">
                                            <legend aria-label="Date">Date</legend>
                                            <md-datepicker
                                                ng-model="value"
                                                md-open-on-focus
                                                aria-label="Date">
                                            </md-datepicker>
                                        </fieldset>
                                    </md-input-container>
                                `
			}
                    </div>
                </md-dialog-content>
                <md-dialog-actions layout="row">
                    <span flex></span>
                    <md-button ng-click="close()">Cancel</md-button>
                    <md-button ng-click="save()" class="md-raised md-accent">Save</md-button>
                </md-dialog-actions>
            </md-dialog>
        `,
			parent: angular.element(document.body),
			targetEvent: ev,
			clickOutsideToClose: true,
			fullscreen: false,
			locals: { currentValue }
		}).then(updatedValue => {
			if (fieldKey === 'financial_capacity') {
				this.assessmentData.financial_capacity.date = updatedValue.date;
				this.assessmentData.financial_capacity.result = updatedValue.result;
				this.assessmentData.assessment_comment = updatedValue.text;
			} else {
				this.assessmentData[fieldKey] = updatedValue;
			}

			this.saveData(this.preparePayload());
		});
	}



	getPublicCoordinatorText(value) {
		if (parseInt(value) === 1) {
			return "Yes";
		} else if (parseInt(value) === 0) {
			return "No";
		}
		return "-";
	}
}